import * as React from "react";
import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { StrapiPromo } from "../types";

import { resizeImgCloudFront } from "@skv/util";

type ImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

export interface AdSpaceWithImageProps {
  imgXl2x: string;
  imgXl: string;

  imgLg2x: string;
  imgLg: string;

  imgMd2x: string;
  imgMd: string;

  img2x: string;
  img: string;

  imgAlt?: string;

  imgProps?: Omit<ImgProps, "alt" | "src" | "css">;
}

const BaseAdSpace = styled.section`
  background-color: ${(p): string => p.theme.infoColor};
  display: flex;
  flex-direction: column;
  min-height: 320px;
  justify-content: center;

  @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
    flex-direction: row;
  }
`;

const BaseAdSpaceContent = styled.div`
  padding: 1rem ${30 / 16}rem;
  flex: 1 1 auto;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
    flex: 0 1 40%;
  }
`;

export const AdSpaceWithImage = ({
  imgXl2x,
  imgXl,

  imgLg2x,
  imgLg,

  imgMd2x,
  imgMd,

  img2x,
  img,
  imgAlt = "",

  imgProps,

  children,
  ...rest
}: AdSpaceWithImageProps & {
  children?: React.ReactNode;
}): JSX.Element => {
  const theme = useTheme();

  return (
    <BaseAdSpace {...rest}>
      <div
        css={`
          flex: 1 1 auto;
          position: relative;

          min-height: 175px;

          @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
            min-height: 320px;
            flex: 1 1 60%;
          }
        `}
      >
        <picture>
          <source
            srcSet={[imgXl, `${imgXl2x} 2x`].join(", ")}
            media={`(min-width: ${theme.xlBreakpoint})`}
          />
          <source
            srcSet={[imgLg, `${imgLg2x} 2x`].join(", ")}
            media={`(min-width: ${theme.lgBreakpoint})`}
          />
          <source
            srcSet={[imgMd, `${imgMd2x} 2x`].join(", ")}
            media={`(min-width: ${theme.mdBreakpoint})`}
          />
          <source srcSet={[img, `${img2x} 2x`].join(", ")} />
          <img
            css={`
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: block;
              max-width: 100%;
              object-fit: cover;
              object-position: 0% 50%;
            `}
            src={img2x}
            alt={imgAlt}
            {...imgProps}
          />
        </picture>
      </div>
      <BaseAdSpaceContent>{children}</BaseAdSpaceContent>
    </BaseAdSpace>
  );
};

export const AdSpace = ({
  created_at,
  heading,
  subheading,
  id,
  media,
  published_at,
  summary,
  updated_at,
  url,
  media_alt_tag,
  _request_date_UTC,
  button_text,
  object_position,
  children,
  ...rest
}: StrapiPromo & {
  children?: React.ReactNode;
}): JSX.Element => {
  if (media) {
    return (
      <AdSpaceWithImage
        imgProps={{
          style: {
            objectPosition: object_position || "50% 50%",
          },
        }}
        imgXl2x={media.extra_large2x.url}
        imgXl={resizeImgCloudFront(media.extra_large2x.url, "1180x320")}
        imgLg2x={media.large2x.url}
        imgLg={resizeImgCloudFront(media.large2x.url, "990x320")}
        imgMd2x={media.medium2x.url}
        imgMd={resizeImgCloudFront(media.medium2x.url, "767x320")}
        img2x={media.small2x.url}
        img={resizeImgCloudFront(media.small2x.url, "575x175")}
        imgAlt={media_alt_tag || heading}
      >
        <div>
          <h2>{heading}</h2>
          {subheading && (
            <h3 className="h4 font-weight-normal">{subheading}</h3>
          )}
          {summary && <p>{summary}</p>}
          <div className="mt-4">
            <Link
              css={`
                min-width: 160px;
                align-self: flex-start;
              `}
              className="btn btn-sm btn-outline-light"
              to={url}
            >
              {button_text || "Read more"}
            </Link>
          </div>
        </div>
      </AdSpaceWithImage>
    );
  }

  return (
    <BaseAdSpace {...rest}>
      <BaseAdSpaceContent
        css={`
          padding: 3rem ${30 / 16}rem;

          @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
            flex: 1 1 100%;
            text-align: center;
          }
        `}
      >
        <div>
          <h2
            css={`
              font-size: 3rem;
            `}
          >
            {heading}
          </h2>
          {subheading && (
            <h3 className="h4 font-weight-normal">{subheading}</h3>
          )}
          {summary && <p>{summary}</p>}
          <div className="mt-4">
            <Link
              css={`
                min-width: 160px;
                align-self: flex-start;
                @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
                  align-self: center;
                }
              `}
              className="btn btn-sm btn-outline-light"
              to={url}
            >
              {button_text || "Read more"}
            </Link>
          </div>
        </div>
      </BaseAdSpaceContent>
    </BaseAdSpace>
  );
};
